import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, withModifiers as _withModifiers, Transition as _Transition, withCtx as _withCtx, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  class: "bc-box",
  role: "dialog",
  "aria-label": "Enviar aviso para agentes"
}
const _hoisted_2 = { class: "bc-field" }
const _hoisted_3 = { class: "bc-ta-wrap" }
const _hoisted_4 = { class: "bc-field-row" }
const _hoisted_5 = { class: "bc-field" }
const _hoisted_6 = {
  key: 0,
  class: "bc-preview"
}
const _hoisted_7 = ["src"]
const _hoisted_8 = ["src"]
const _hoisted_9 = { class: "bc-field" }
const _hoisted_10 = { class: "bc-agents-head" }
const _hoisted_11 = { class: "bc-agents-label" }
const _hoisted_12 = { class: "bc-agents-list" }
const _hoisted_13 = {
  key: 0,
  class: "bc-agents-empty"
}
const _hoisted_14 = {
  key: 1,
  class: "bc-agents-empty"
}
const _hoisted_15 = ["checked", "onChange"]
const _hoisted_16 = ["src", "alt"]
const _hoisted_17 = { key: 1 }
const _hoisted_18 = { class: "bc-agent-name" }
const _hoisted_19 = {
  key: 1,
  class: "bc-error"
}
const _hoisted_20 = { class: "bc-actions" }
const _hoisted_21 = ["disabled"]
const _hoisted_22 = ["disabled"]

import { ref, computed, nextTick, watch } from 'vue'
import { useStore } from 'vuex'
import Swal from 'sweetalert2'
import EmojiPicker from '@/components/dashboard/EmojiPicker.vue'
import { avatarColor, initials } from '@/composables/useHelpers'
import { listAgents } from '@/services/wootdashAgents'
import { toMediaUrl } from '@/utils/media'
import { useBroadcast } from '@/composables/useBroadcast'
import { broadcastComposeOpen, closeBroadcastCompose } from '@/composables/useBroadcastCompose'
import { parseYoutubeId } from '@/utils/youtube'
import type { AuthUser } from '@/types/auth'


export default /*@__PURE__*/_defineComponent({
  __name: 'BroadcastComposeModal',
  setup(__props) {

const store = useStore()
const agents = ref<{ id: string; name: string; avatarUrl: string | null }[]>([])
const loadingAgents = ref(false)

async function loadAgents(token: string, myId: string) {
  loadingAgents.value = true
  try {
    const { agents: list } = await listAgents(token)
    agents.value = list
      .filter(a => a.isActive && a.id !== myId)
      .map(a => ({ id: a.id, name: `${a.firstName} ${a.lastName}`.trim(), avatarUrl: a.avatarUrl }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
  } finally {
    loadingAgents.value = false
  }
}
const { sending, sendError, sendBroadcast } = useBroadcast()

const taEl = ref<HTMLTextAreaElement | null>(null)
const body = ref('')
const imageUrl = ref('')
const youtubeUrl = ref('')
const linkUrl = ref('')
const agentSearch = ref('')
const selectedAgentIds = ref<string[]>([])
const imageLoadFailed = ref(false)

const filteredAgents = computed(() => {
  const q = agentSearch.value.toLowerCase()
  return agents.value.filter(a => !q || a.name.toLowerCase().includes(q))
})

const allSelected = computed(() => agents.value.length > 0 && selectedAgentIds.value.length === agents.value.length)

const canSubmit = computed(() =>
  body.value.trim().length > 0 && selectedAgentIds.value.length > 0 && !sending.value
)

const youtubeEmbedUrl = computed(() => {
  if (!youtubeUrl.value) return null
  const id = parseYoutubeId(youtubeUrl.value)
  return id ? `https://www.youtube.com/embed/${id}` : null
})

function onImageError(e: Event) {
  imageLoadFailed.value = true
  ;(e.target as HTMLImageElement).style.display = 'none'
}

// Mesmo padrão de inserção com cursor de FloatChatWindow.vue (insertEmoji).
function insertEmoji(em: string) {
  const el = taEl.value
  if (!el) { body.value += em; return }
  const start = el.selectionStart ?? body.value.length
  const end   = el.selectionEnd   ?? body.value.length
  body.value = body.value.slice(0, start) + em + body.value.slice(end)
  nextTick(() => {
    el.selectionStart = el.selectionEnd = start + em.length
    el.focus()
  })
}

function toggleAgent(id: string) {
  const idx = selectedAgentIds.value.indexOf(id)
  if (idx === -1) selectedAgentIds.value.push(id)
  else selectedAgentIds.value.splice(idx, 1)
}

function toggleSelectAll() {
  selectedAgentIds.value = allSelected.value ? [] : agents.value.map(a => a.id)
}

function resetForm() {
  body.value = ''
  imageUrl.value = ''
  youtubeUrl.value = ''
  linkUrl.value = ''
  agentSearch.value = ''
  selectedAgentIds.value = []
  imageLoadFailed.value = false
  sendError.value = ''
}

function close() {
  if (sending.value) return
  closeBroadcastCompose()
  resetForm()
}

async function submit() {
  if (!canSubmit.value) return
  const user = store.getters.authUser as AuthUser | null
  if (!user) return

  try {
    await sendBroadcast({
      body:         body.value.trim(),
      imageUrl:     imageUrl.value || undefined,
      youtubeUrl:   youtubeUrl.value || undefined,
      linkUrl:      linkUrl.value || undefined,
      recipientIds: selectedAgentIds.value,
    })
    closeBroadcastCompose()
    resetForm()
    Swal.fire({ icon: 'success', title: 'Aviso enviado!', timer: 2000, showConfirmButton: false })
  } catch {
    // sendError já foi setado por useBroadcast — segue exibido no modal, que permanece aberto.
  }
}

// Carrega a lista de agentes só se ainda não tiver sido carregada por outro
// consumidor (AgentChatDrawer.vue já chama loadAgents no mount da app) —
// evita repetir a chamada sempre que o compositor abrir.
watch(broadcastComposeOpen, (open) => {
  if (!open) return
  const user = store.getters.authUser as AuthUser | null
  if (user?.wootdash_token && !agents.value.length && !loadingAgents.value) {
    loadAgents(user.wootdash_token, user.wootdash_agent_id ?? '')
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(_Transition, { name: "bc-fade" }, {
      default: _withCtx(() => [
        (_unref(broadcastComposeOpen))
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "bc-overlay",
              onMousedown: _withModifiers(close, ["self"])
            }, [
              _createElementVNode("div", _hoisted_1, [
                _cache[6] || (_cache[6] = _createElementVNode("div", { class: "bc-icon" }, "📢", -1)),
                _cache[7] || (_cache[7] = _createElementVNode("div", { class: "bc-title" }, "Aviso para agentes", -1)),
                _cache[8] || (_cache[8] = _createElementVNode("div", { class: "bc-desc" }, "Escreva uma mensagem e escolha quem vai receber. Cada agente confirma o recebimento na própria tela.", -1)),
                _createElementVNode("div", _hoisted_2, [
                  _createElementVNode("div", _hoisted_3, [
                    _withDirectives(_createElementVNode("textarea", {
                      ref_key: "taEl",
                      ref: taEl,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((body).value = $event)),
                      class: "bc-textarea",
                      rows: "4",
                      placeholder: "Escreva o aviso…",
                      maxlength: "2000"
                    }, null, 512), [
                      [_vModelText, body.value]
                    ]),
                    _createVNode(EmojiPicker, {
                      class: "bc-emoji",
                      onPick: insertEmoji
                    })
                  ])
                ]),
                _createElementVNode("div", _hoisted_4, [
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((imageUrl).value = $event)),
                    type: "text",
                    class: "bc-input",
                    placeholder: "URL de imagem (opcional)"
                  }, null, 512), [
                    [
                      _vModelText,
                      imageUrl.value,
                      void 0,
                      { trim: true }
                    ]
                  ]),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((youtubeUrl).value = $event)),
                    type: "text",
                    class: "bc-input",
                    placeholder: "Link do YouTube (opcional)"
                  }, null, 512), [
                    [
                      _vModelText,
                      youtubeUrl.value,
                      void 0,
                      { trim: true }
                    ]
                  ])
                ]),
                _createElementVNode("div", _hoisted_5, [
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((linkUrl).value = $event)),
                    type: "text",
                    class: "bc-input",
                    placeholder: "Link (opcional)"
                  }, null, 512), [
                    [
                      _vModelText,
                      linkUrl.value,
                      void 0,
                      { trim: true }
                    ]
                  ])
                ]),
                (imageUrl.value || youtubeEmbedUrl.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                      (imageUrl.value)
                        ? (_openBlock(), _createElementBlock("img", {
                            key: 0,
                            src: imageUrl.value,
                            alt: "Preview",
                            class: "bc-preview-img",
                            onError: onImageError
                          }, null, 40, _hoisted_7))
                        : _createCommentVNode("", true),
                      (youtubeEmbedUrl.value)
                        ? (_openBlock(), _createElementBlock("iframe", {
                            key: 1,
                            src: youtubeEmbedUrl.value,
                            class: "bc-preview-video",
                            frameborder: "0",
                            allowfullscreen: ""
                          }, null, 8, _hoisted_8))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("div", _hoisted_10, [
                    _createElementVNode("span", _hoisted_11, "Destinatários (" + _toDisplayString(selectedAgentIds.value.length) + ")", 1),
                    _createElementVNode("button", {
                      type: "button",
                      class: "bc-link-btn",
                      onClick: toggleSelectAll
                    }, _toDisplayString(allSelected.value ? 'Limpar seleção' : 'Selecionar todos'), 1)
                  ]),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((agentSearch).value = $event)),
                    type: "text",
                    class: "bc-input bc-agents-search",
                    placeholder: "Buscar agente…"
                  }, null, 512), [
                    [
                      _vModelText,
                      agentSearch.value,
                      void 0,
                      { trim: true }
                    ]
                  ]),
                  _createElementVNode("div", _hoisted_12, [
                    (loadingAgents.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_13, "Carregando agentes…"))
                      : (!filteredAgents.value.length)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_14, "Nenhum agente encontrado"))
                        : _createCommentVNode("", true),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredAgents.value, (a) => {
                      return (_openBlock(), _createElementBlock("label", {
                        key: a.id,
                        class: "bc-agent-row"
                      }, [
                        _createElementVNode("input", {
                          type: "checkbox",
                          checked: selectedAgentIds.value.includes(a.id),
                          onChange: ($event: any) => (toggleAgent(a.id))
                        }, null, 40, _hoisted_15),
                        _createElementVNode("span", {
                          class: "bc-agent-av",
                          style: _normalizeStyle({ background: _unref(avatarColor)(a.name) })
                        }, [
                          (a.avatarUrl)
                            ? (_openBlock(), _createElementBlock("img", {
                                key: 0,
                                src: _unref(toMediaUrl)(a.avatarUrl)!,
                                alt: a.name,
                                onError: _cache[5] || (_cache[5] = (e) => (e.target as HTMLImageElement).style.display='none')
                              }, null, 40, _hoisted_16))
                            : (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(_unref(initials)(a.name)), 1))
                        ], 4),
                        _createElementVNode("span", _hoisted_18, _toDisplayString(a.name), 1)
                      ]))
                    }), 128))
                  ])
                ]),
                (_unref(sendError))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_19, _toDisplayString(_unref(sendError)), 1))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_20, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "bc-btn bc-btn--muted",
                    disabled: _unref(sending),
                    onClick: close
                  }, "Cancelar", 8, _hoisted_21),
                  _createElementVNode("button", {
                    type: "button",
                    class: "bc-btn bc-btn--primary",
                    disabled: !canSubmit.value,
                    onClick: submit
                  }, _toDisplayString(_unref(sending) ? 'Enviando…' : 'Enviar aviso'), 9, _hoisted_22)
                ])
              ])
            ], 32))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})